import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Tooltip, Alert } from 'antd';

import {
  UserOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

import {
  Form as FormikForm,
  FormItem,
  Input,
  Radio,
  DatePicker,
  Select,
} from 'formik-antd';

import {
  simpleFilterOptions,
  getIsUploading,
  getUrlFromEvent,
  titleOptions,
  genderOptions,
  DEFAULT_BIRTH_YEAR,
  over18,
} from 'utils/form';

import { dateFormat } from 'utils/dates';
import { Upload, Fieldset } from 'components/form';
import nationalities from 'utils/nationalities';
import RightToWork from 'components/right-to-work';
import StagePropTypes from './prop-types';
import { DB_PREFIX } from './constants';

const helpDOB = 'To comply with regulations you have to be over 18 years old';

const helpProfile = `
This information will help potential employers indentify you when you arrive for your shifts.
This personal data will only be shared once you have accepted a shift.`;

const helpGender = `
This is sometimes needed if the employers requires specific protected characteristics for the service and which is exempt under the Equality Act 2010.
`;

const Form = ({ isSubmitting, values, setFieldValue, uploadTo, children }) => {
  const [uploading, setUploading] = useState(false);

  return (
    <FormikForm layout="vertical">
      <FormItem label="Profile picture" name={`${DB_PREFIX}.photoURL`}>
        <Upload
          listType="picture-card"
          className="avatar-uploader"
          showUploadList={false}
          uploadTo={uploadTo}
          multiple={false}
          disabled={uploading || isSubmitting}
          onChange={(e) => {
            setUploading(getIsUploading(e));
            setFieldValue(`${DB_PREFIX}.photoURL`, getUrlFromEvent(e));
          }}
        >
          {values[DB_PREFIX].photoURL ? (
            <img src={values[DB_PREFIX].photoURL} alt="avatar" />
          ) : (
            <div>
              {uploading ? <LoadingOutlined /> : <PlusOutlined />}
              <div className="ant-upload-text">
                {uploading ? 'Uploading' : 'Upload'}
              </div>
            </div>
          )}
        </Upload>
      </FormItem>

      <Alert description={helpProfile} type="info" showIcon />

      <Fieldset>
        <FormItem label="Preferred title" name={`${DB_PREFIX}.title`}>
          <Radio.Group name={`${DB_PREFIX}.title`}>
            {titleOptions.map(({ value, label }) => (
              <Radio.Button key={value} value={value}>
                {label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </FormItem>
        <FormItem label="First Name" name={`${DB_PREFIX}.firstName`}>
          <Input
            name={`${DB_PREFIX}.firstName`}
            placeholder="Name"
            prefix={<UserOutlined />}
          />
        </FormItem>
        <FormItem label="Last Name" name={`${DB_PREFIX}.lastName`}>
          <Input
            name={`${DB_PREFIX}.lastName`}
            placeholder="Last name"
            prefix={<UserOutlined />}
          />
        </FormItem>
      </Fieldset>

      <Fieldset>
        <FormItem
          name={`${DB_PREFIX}.gender`}
          label={
            <span>
              Gender&nbsp;
              <Tooltip title={helpGender}>
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
        >
          <Radio.Group name={`${DB_PREFIX}.gender`}>
            {genderOptions.map(({ value, label }) => (
              <Radio.Button key={value} value={value}>
                {label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </FormItem>
      </Fieldset>

      <Fieldset>
        <FormItem
          name="dateOfBirth"
          label={
            <span>
              Date Of Birth
              <Tooltip title={helpDOB}>
                <InfoCircleOutlined />
              </Tooltip>
            </span>
          }
        >
          <DatePicker
            style={{ width: '100%' }}
            name={`${DB_PREFIX}.dateOfBirth`}
            disabledDate={over18}
            format={dateFormat}
            defaultPickerValue={moment()
              .clone()
              .set('year', DEFAULT_BIRTH_YEAR)}
          />
        </FormItem>

        <FormItem label="Mobile phone number" name={`${DB_PREFIX}.mobilePhone`}>
          <Input name={`${DB_PREFIX}.mobilePhone`} />
        </FormItem>
      </Fieldset>

      <FormItem name={`${DB_PREFIX}.nationality`} label="Nationality">
        {values[DB_PREFIX].nationality !== 'British' &&
        values[DB_PREFIX].nationality != null ? (
          <RightToWork />
        ) : null}
        <Select
          name={`${DB_PREFIX}.nationality`}
          placeholder="Please select your nationality"
          showSearch
          filterOption={simpleFilterOptions}
          allowClear
        >
          {nationalities.map((value, i) => (
            <Select.Option key={`${value}-${i}`} value={value}>
              {value}
            </Select.Option>
          ))}
        </Select>
      </FormItem>

      {children}
    </FormikForm>
  );
};

Form.propTypes = {
  uploadTo: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool,
  setFieldValue: PropTypes.func.isRequired,
  values: StagePropTypes.isRequired,
  children: PropTypes.node,
};

Form.defaultProps = {
  isSubmitting: false,
  children: null,
};

export default Form;
